import { useState } from 'react'
import clsx from 'clsx'
import type { Checklist as ChecklistModel } from 'lib/community/discussions-writer'
import { checklistCount, visibleChecklistItems } from 'lib/community/writer-phone'
import styles from './Writer.module.css'

/**
 * The checklist on a phone (design M1c): a compact light-blue card right under the title — its
 * heading and "{done} of {total}", the ticked items and the next one to do; a tap shows them all
 * (and a second tap folds them back). Advice only, as the desktop card: it never blocks posting.
 * Rows are a flex column with a gap, so two can never overlap. Hidden above 767.98px, where the
 * side card shows the whole list.
 */
const PhoneChecklist: React.VFC<{ checklist: ChecklistModel }> = ({ checklist }) => {
  const [expanded, setExpanded] = useState(false)
  const { items, hidden } = visibleChecklistItems(checklist.items, expanded)
  const foldable = expanded || hidden > 0
  return (
    <section className={styles.phoneChecks} aria-label="Checklist">
      <button
        type="button"
        className={styles.phoneChecksHead}
        aria-expanded={foldable ? expanded : undefined}
        aria-controls="writer-phone-checks"
        disabled={!foldable}
        onClick={() => setExpanded(on => !on)}
      >
        <span>{checklist.title}</span>
        <span className={styles.phoneChecksCount}>{checklistCount(checklist.items)}</span>
      </button>
      <ul id="writer-phone-checks" className={styles.phoneCheckList}>
        {items.map(item => (
          <li
            key={item.label}
            className={clsx(styles.phoneCheck, { [styles.phoneCheckDone]: item.done })}
          >
            <span className={styles.phoneCheckMark} aria-hidden="true">
              {item.done ? '✓' : ''}
            </span>
            <span>
              {item.label}
              <span className={styles.srOnly}>{item.done ? ' (done)' : ' (not yet)'}</span>
            </span>
          </li>
        ))}
      </ul>
    </section>
  )
}

export default PhoneChecklist
